// src/components/PrioritySelect.tsx
// Inline priority selector with dropdown menu showing colored priority icons.

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { cn } from '@core/ui';
import { AnchoredMenu } from './AnchoredMenu';
import { useI18n } from '@react/hooks';
import {
  taskPriorityMap,
  taskPriorityOptions,
  getStatusMapping,
  getPriorityIconPath,
  getPriorityColor,
} from '@/features/task/logic';

export interface PrioritySelectProps {
  /** Current priority value (e.g. 'TASK_PRIORITY_HIGH') */
  value: string;
  /** Callback when a new priority is selected */
  onSelect: (priority: string) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Icon-only trigger with no border, label or chevron — for dense rows, where
   * one bordered control per task would make the list read as a form. The label
   * stays available through `aria-label` and the tooltip.
   */
  compact?: boolean;
}

function PriorityIcon({ priority, className }: { priority: string; className?: string }) {
  const iconPath = getPriorityIconPath(priority);
  const colorClass = getPriorityColor(priority);
  if (!iconPath) return null;

  return (
    <svg
      className={cn('h-4 w-4 shrink-0', colorClass, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={iconPath} />
    </svg>
  );
}

export function PrioritySelect({
  value,
  onSelect,
  disabled = false,
  className,
  compact = false,
}: PrioritySelectProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const menuId = useId();

  const currentMapping = getStatusMapping(taskPriorityMap, value);

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  // Outside click, Escape and repositioning live in AnchoredMenu; what stays
  // here is the option keyboard navigation and selection.

  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const item = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, open]);

  const handleSelect = (priorityValue: string) => {
    onSelect(priorityValue);
    close();
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = taskPriorityOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else if (focusedIndex >= 0) {
          handleSelect(taskPriorityOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = taskPriorityOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else {
          setFocusedIndex((prev) =>
            prev + 1 < taskPriorityOptions.length ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
    }
  };

  const dropdown = (
    <AnchoredMenu
      anchor={triggerRef}
      open={open}
      onClose={close}
      ref={listRef}
      as="ul"
      role="listbox"
      id={menuId}
      ariaLabel={t('task.field.priority')}
      ariaActivedescendant={focusedIndex >= 0 ? `${menuId}-option-${focusedIndex}` : undefined}
      matchTriggerWidth
      maxHeight={240}
      className="py-1"
    >
        {taskPriorityOptions.map((option, index) => {
          const isSelected = option.value === value;
          const isFocused = index === focusedIndex;
          return (
            <li
              key={option.value}
              id={`${menuId}-option-${index}`}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => setFocusedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
              className={cn(
                'cursor-pointer select-none px-3 py-1.5 text-sm flex items-center gap-2',
                isFocused && 'bg-accent text-accent-foreground',
                isSelected && !isFocused && 'bg-accent/50',
              )}
            >
              <PriorityIcon priority={option.value} />
              <span>{t(option.labelKey)}</span>
              {isSelected && (
                <svg className="h-4 w-4 ml-auto text-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </li>
          );
        })}
    </AnchoredMenu>
  );

  return (
    <div className={cn('inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            if (!open) {
              const idx = taskPriorityOptions.findIndex((o) => o.value === value);
              setFocusedIndex(idx >= 0 ? idx : 0);
            }
          }
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('task.field.priority')}
        title={compact ? t(currentMapping.labelKey) : undefined}
        className={cn(
          compact
            ? 'inline-flex items-center justify-center rounded p-1 hover:bg-accent transition-colors'
            : 'w-full inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-muted/50 transition-colors',
          open ? 'ring-2 ring-ring' : '',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        <PriorityIcon priority={value} />
        {/* An unset priority still needs something to aim at */}
        {compact && !getPriorityIconPath(value) && (
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-muted-foreground/30" aria-hidden="true" />
        )}
        {!compact && (
          <>
            <span className="text-foreground flex-1 text-left">{t(currentMapping.labelKey)}</span>
            <svg
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0',
                open && 'rotate-180',
              )}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </>
        )}
      </button>
      {dropdown}
    </div>
  );
}
